import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle, ShareNetwork, Receipt, House, MapPin, CalendarBlank } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { fileUrl, formatINR, API_BASE } from "@/lib/api";
import { SITE_URL } from "@/components/Seo";
import { Spinner } from "@/components/Loaders";
import { Button } from "@/components/ui/button";

export default function BookingConfirmed() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => { api.get(`/bookings/${id}`).then((r) => setB(r.data)).catch(() => setErr(true)); }, [id]);

  if (err) return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
      <p className="text-sm text-stone-500">We couldn't load this booking. Please check My Bookings.</p>
      <Button className="mt-4 rounded-xl bg-primary hover:bg-green-900" onClick={() => navigate("/bookings")}>My Bookings</Button>
    </div>
  );
  if (!b) return <div className="flex h-screen items-center justify-center"><Spinner /></div>;

  const shareText = `My booking at ${b.property_name} is confirmed! ${b.booking_ref} · ${b.date} — booked on Avinya Bookings.`;
  const shareUrl = `${SITE_URL}/bookings/${b.id}`;

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Booking Confirmed — Avinya Bookings", text: shareText, url: shareUrl }); return; } catch { /* cancelled */ }
    }
    try { await navigator.clipboard.writeText(`${shareText} ${shareUrl}`); toast.success("Booking details copied to clipboard!"); }
    catch { toast.error("Could not share right now."); }
  };

  const downloadReceipt = () => {
    const token = localStorage.getItem("avb_token");
    fetch(`${API_BASE}/bookings/${b.id}/invoice/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = u; a.download = `receipt_${b.booking_ref}.pdf`; a.click();
        URL.revokeObjectURL(u);
      })
      .catch(() => toast.error("Receipt isn't available yet. Try again from My Bookings shortly."));
  };

  const num = (v) => v || 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white px-4 pb-12 pt-10" data-testid="booking-confirmed-page">
      <div className="mx-auto max-w-lg animate-fade-up text-center">
        <div className="relative mx-auto flex h-24 w-24 items-center justify-center">
          <span className="absolute inset-0 animate-ping rounded-full bg-green-200/60" />
          <CheckCircle size={88} weight="fill" className="relative text-primary" />
        </div>
        <h1 className="mt-5 font-heading text-2xl font-bold text-stone-900 md:text-3xl" data-testid="confirmed-title">Booking Confirmed</h1>
        <p className="mt-2 text-sm text-stone-500">Your advance is paid and your reservation is locked in. A confirmation has been sent to you.</p>

        <div className="mt-6 overflow-hidden rounded-2xl border border-stone-200 bg-white text-left shadow-sm">
          <div className="flex items-center gap-3 border-b border-stone-100 p-4">
            <img src={fileUrl(b.property_image)} alt={b.property_name} className="h-16 w-16 rounded-xl object-cover" />
            <div className="min-w-0">
              <p className="truncate font-heading font-semibold text-stone-900">{b.property_name}</p>
              <p className="flex items-center gap-1 text-sm text-stone-500"><MapPin size={14} weight="fill" className="text-primary" /> {b.property_location}</p>
            </div>
          </div>
          <div className="space-y-2.5 p-4 text-sm">
            <div className="flex justify-between"><span className="text-stone-500">Booking ID</span><span data-testid="confirmed-ref" className="font-mono font-semibold text-stone-800">{b.booking_ref}</span></div>
            <div className="flex justify-between"><span className="text-stone-500">Date</span><span className="inline-flex items-center gap-1 font-medium text-stone-800"><CalendarBlank size={14} /> {b.date}</span></div>
            <div className="flex justify-between"><span className="text-stone-500">Guests · Type</span><span className="font-medium text-stone-800">{b.guests} · {b.booking_type}</span></div>
            <div className="flex justify-between"><span className="text-stone-500">Advance paid</span><span className="font-heading font-bold text-primary">{formatINR(num(b.advance_amount))}</span></div>
            <div className="flex justify-between"><span className="text-stone-500">{b.balance_paid ? "Balance paid" : "Balance at venue"}</span><span className="font-medium text-stone-800">{formatINR(num(b.balance_amount))}</span></div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <Button data-testid="confirmed-share" variant="outline" onClick={share} className="gap-2 rounded-xl py-6"><ShareNetwork size={18} /> Share</Button>
          <Button data-testid="confirmed-receipt" variant="outline" onClick={downloadReceipt} className="gap-2 rounded-xl py-6"><Receipt size={18} /> Download Receipt</Button>
        </div>
        <div className="mt-3 flex gap-3">
          <Button data-testid="confirmed-home" variant="ghost" onClick={() => navigate("/")} className="flex-1 gap-2 rounded-xl text-stone-600"><House size={18} /> Home</Button>
          <Button data-testid="confirmed-view" onClick={() => navigate(`/bookings/${b.id}`)} className="flex-1 rounded-xl bg-primary hover:bg-green-900">View Booking</Button>
        </div>
      </div>
    </div>
  );
}
